import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, User, Phone } from 'lucide-react';
import api from '../../api';

const getMinDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' });
const MONTH_NAMES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const DAY_NAMES = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

export default function PublicBooking() {
  const { slug } = useParams();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsClosed, setSlotsClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const voucherRef = useRef(null);

  const totalCost = selectedServices.reduce((s, x) => s + (x.cost || 0), 0);
  const totalDuration = selectedServices.reduce((s, x) => s + (x.duration_minutes || 0), 0);

  useEffect(() => {
    // Fix scroll: override app's overflow:hidden for this public page
    document.body.classList.add('public-page');
    document.body.style.overflow = 'auto';
    document.body.style.height = 'auto';
    const root = document.getElementById('root');
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; root.style.maxWidth = '100%'; }
    return () => {
      document.body.classList.remove('public-page');
      document.body.style.overflow = '';
      document.body.style.height = '';
      if (root) { root.style.overflow = ''; root.style.height = ''; root.style.maxWidth = ''; }
    };
  }, []);

  useEffect(() => {
    api.get(`/booking/${slug}`)
      .then(r => { setBusiness(r.data); setLoading(false); })
      .catch(e => { setError(e.response?.data?.detail || 'Negocio no encontrado'); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (!business || !selectedDate) { setSlots([]); return; }
    setSlotsLoading(true);
    setSelectedSlot('');
    const params = { date: selectedDate };
    if (selectedServices.length > 0) params.service_ids = selectedServices.map(s => s.id).join(',');
    if (selectedStaff) params.staff_name = selectedStaff.staff_name;
    api.get(`/booking/${slug}/available-slots`, { params })
      .then(r => { setSlots(r.data.slots || []); setSlotsClosed(r.data.closed || false); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, selectedServices, selectedStaff, business, slug]);

  const toggleService = (s) => {
    setSelectedServices(prev => prev.find(x => x.id === s.id) ? prev.filter(x => x.id !== s.id) : [...prev, s]);
  };

  const refreshSlots = () => {
    if (!business || !selectedDate) return;
    setSlotsLoading(true);
    const params = { date: selectedDate };
    if (selectedServices.length > 0) params.service_ids = selectedServices.map(s => s.id).join(',');
    if (selectedStaff) params.staff_name = selectedStaff.staff_name;
    api.get(`/booking/${slug}/available-slots`, { params })
      .then(r => { setSlots(r.data.slots || []); setSlotsClosed(r.data.closed || false); })
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  };

  const downloadAsImage = async () => {
    if (!voucherRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(voucherRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const fileName = `turno-${(business.business_name || 'turno').replace(/\s+/g, '-').toLowerCase()}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });

      // Preferimos Web Share API (abre menú nativo en celular)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Turno en ${business.business_name}`,
          text: `Turno reservado para ${name} el ${selectedDate.split('-').reverse().join('/')} a las ${selectedSlot}`,
        });
      } else {
        // Fallback: abrir imagen en nueva pestaña
        const url = URL.createObjectURL(blob);
        const opened = window.open(url, '_blank');
        if (!opened) {
          // Si el popup fue bloqueado, descarga directa
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          link.click();
        }
      }
    } catch (e) {
      if (e?.name !== 'AbortError') { console.error(e); alert('Error al generar imagen'); }
    } finally { setDownloading(false); }
  };

  const downloadAsPDF = async () => {
    if (!voucherRef.current) return;
    setDownloading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { jsPDF } = await import('jspdf');
      const canvas = await html2canvas(voucherRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      const imgData = canvas.toDataURL('image/png');
      const pxW = canvas.width / 2;
      const pxH = canvas.height / 2;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [pxW, pxH] });
      pdf.addImage(imgData, 'PNG', 0, 0, pxW, pxH);

      // Abrir PDF en nueva pestaña para que el cliente lo vea y comparta
      const pdfBlobUrl = pdf.output('bloburl');
      const opened = window.open(pdfBlobUrl, '_blank');
      if (!opened) {
        // Si el popup fue bloqueado, descarga directa
        pdf.save(`turno-${(business.business_name || 'turno').replace(/\s+/g, '-').toLowerCase()}.pdf`);
      }
    } catch (e) { console.error(e); alert('Error al generar PDF'); }
    finally { setDownloading(false); }
  };

  const handleSubmit = async () => {
    if (!name.trim()) { alert('Ingresá tu nombre'); return; }
    if (!phone.trim()) { alert('Ingresá tu celular'); return; }
    if (!selectedDate) { alert('Elegí una fecha'); return; }
    if (!selectedSlot) { alert('Elegí un horario'); return; }
    setSaving(true);
    try {
      await api.post(`/booking/${slug}`, {
        client_name: name.trim(),
        client_phone: phone.trim(),
        date: selectedDate,
        time: selectedSlot,
        service_ids: selectedServices.map(s => s.id),
        staff_name: selectedStaff?.staff_name || null,
      });
      setSubmitted(true);
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || 'Error al reservar, intentá de nuevo';
      alert(msg);
      // If slot conflict (409), refresh available slots and clear selection
      if (status === 409) {
        setSelectedSlot('');
        refreshSlots();
      }
    }
    finally { setSaving(false); }
  };

  const isEstetica = business?.business_type?.toLowerCase().includes('est') || business?.business_type?.toLowerCase().includes('spa');
  const accentColor = isEstetica ? '#ec4899' : '#5de6c8';
  const bgColor = isEstetica ? '#1a0a12' : '#0e1117';

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgColor }}><div className="spinner" /></div>;
  if (error) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bgColor, color: '#fff', flexDirection: 'column', gap: 12 }}><p style={{ fontSize: '1.2rem' }}>{error}</p></div>;

  if (submitted) {
    const detailRows = [
      { label: 'Nombre', value: name },
      ...(selectedServices.length > 0 ? [{ label: 'Servicios', value: selectedServices.map(s => s.name).join(' + ') }] : []),
      ...(selectedStaff ? [{ label: 'Profesional', value: selectedStaff.staff_name }] : []),
      { label: 'Fecha', value: selectedDate.split('-').reverse().join('/') },
      { label: 'Hora', value: selectedSlot },
    ];
    return (
      <div style={{ minHeight: '100vh', overflowY: 'auto', background: bgColor, padding: '32px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', WebkitOverflowScrolling: 'touch' }}>

        {/* Voucher card (this is what gets captured) */}
        <div ref={voucherRef} style={{ width: '100%', maxWidth: 360, borderRadius: 16, overflow: 'hidden', boxShadow: `0 0 40px ${accentColor}40`, marginBottom: 20, fontFamily: 'system-ui, -apple-system, Helvetica, Arial, sans-serif' }}>

          {/* Header with business name */}
          <div style={{ background: accentColor, padding: '22px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 900, color: isEstetica ? '#fff' : '#0a0f0c', letterSpacing: '-0.5px' }}>
              {business.business_name}
            </div>
            <div style={{ fontSize: '0.78rem', color: isEstetica ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.5)', marginTop: 5, letterSpacing: 1, textTransform: 'uppercase' }}>
              Comprobante de Turno
            </div>
          </div>

          {/* Check mark + title */}
          <div style={{ background: isEstetica ? '#2a0d1c' : '#071410', padding: '28px 24px', textAlign: 'center' }}>
            <div style={{
              width: 68, height: 68, borderRadius: '50%', background: accentColor,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 14px', fontSize: '32px', fontWeight: 900,
              color: isEstetica ? '#fff' : '#071410',
            }}>
              &#10003;
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
              ¡Turno Reservado!
            </div>
          </div>

          {/* Details */}
          <div style={{ background: bgColor, padding: '20px 24px' }}>
            {detailRows.map((item, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                paddingBottom: i < detailRows.length - 1 ? 12 : 0,
                marginBottom: i < detailRows.length - 1 ? 12 : 0,
                borderBottom: i < detailRows.length - 1 ? '1px solid rgba(255,255,255,0.07)' : 'none',
              }}>
                <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', flexShrink: 0 }}>{item.label}</span>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.88rem', textAlign: 'right', marginLeft: 16 }}>{item.value}</span>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div style={{ background: isEstetica ? '#130810' : '#040c09', padding: '12px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.25)' }}>
              Reservado con <span style={{ color: accentColor, fontWeight: 700 }}>Tuki Negocios</span>
            </div>
          </div>
        </div>

        {/* Download buttons */}
        <div style={{ display: 'flex', gap: 12, width: '100%', maxWidth: 360, marginBottom: 20 }}>
          <button
            data-testid="download-png-btn"
            onClick={downloadAsImage}
            disabled={downloading}
            style={{
              flex: 1, padding: '13px 8px', borderRadius: 10,
              border: `2px solid ${accentColor}`, background: 'transparent',
              color: accentColor, fontWeight: 700, fontSize: '0.88rem',
              cursor: downloading ? 'wait' : 'pointer', letterSpacing: 0.3,
            }}>
            {downloading ? 'Generando...' : 'Compartir Imagen'}
          </button>
          <button
            data-testid="download-pdf-btn"
            onClick={downloadAsPDF}
            disabled={downloading}
            style={{
              flex: 1, padding: '13px 8px', borderRadius: 10,
              border: 'none', background: accentColor,
              color: isEstetica ? '#fff' : '#071410', fontWeight: 700, fontSize: '0.88rem',
              cursor: downloading ? 'wait' : 'pointer', letterSpacing: 0.3,
            }}>
            {downloading ? 'Generando...' : 'Abrir PDF'}
          </button>
        </div>

        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', textAlign: 'center', maxWidth: 300 }}>
          Te esperamos. ¡Gracias por reservar!
        </p>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh', overflowY: 'auto', background: bgColor, color: '#fff', fontFamily: 'system-ui, sans-serif', WebkitOverflowScrolling: 'touch' }}>
      {/* Header */}
      <div style={{ background: 'rgba(255,255,255,0.05)', borderBottom: `2px solid ${accentColor}`, padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.5px' }}>{business.business_name}</div>
        <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Reservá tu turno online</div>
      </div>

      <div style={{ maxWidth: 480, margin: '0 auto', padding: '24px 20px' }}>

        {/* Personal Data */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor, letterSpacing: 1, marginBottom: 10 }}>TUS DATOS</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ position: 'relative' }}>
              <User size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
              <input data-testid="booking-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Nombre y Apellido"
                style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.15)`, borderRadius: 10, padding: '12px 12px 12px 36px', color: '#fff', fontSize: '0.95rem', boxSizing: 'border-box' }} />
            </div>
            <div style={{ position: 'relative' }}>
              <Phone size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
              <input data-testid="booking-phone-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Celular"
                style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.15)`, borderRadius: 10, padding: '12px 12px 12px 36px', color: '#fff', fontSize: '0.95rem', boxSizing: 'border-box' }} />
            </div>
          </div>
        </div>

        {/* Service Selection (multi-select) */}
        {business.services?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor, letterSpacing: 1 }}>SERVICIOS (PODÉS ELEGIR VARIOS)</div>
              {selectedServices.length > 0 && (
                <div style={{ fontSize: '0.75rem', color: accentColor, fontWeight: 700 }}>
                  {selectedServices.length} · {totalDuration} min · ${totalCost.toLocaleString('es-AR')}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {business.services.map(s => {
                const checked = !!selectedServices.find(x => x.id === s.id);
                return (
                  <div key={s.id} data-testid={`booking-service-${s.id}`}
                    onClick={() => toggleService(s)}
                    style={{
                      background: checked ? `rgba(${isEstetica ? '236,72,153' : '93,230,200'}, 0.15)` : 'rgba(255,255,255,0.06)',
                      border: `2px solid ${checked ? accentColor : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: 10, padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10
                    }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                      border: `2px solid ${checked ? accentColor : 'rgba(255,255,255,0.3)'}`,
                      background: checked ? accentColor : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {checked && <span style={{ color: isEstetica ? '#fff' : '#0e1117', fontSize: '0.85rem', fontWeight: 800 }}>✓</span>}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{s.name}</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', display: 'flex', gap: 8, marginTop: 2 }}>
                        <span><Clock size={11} style={{ verticalAlign: 'middle' }} /> {s.duration_minutes} min</span>
                      </div>
                    </div>
                    <div style={{ fontWeight: 700, color: accentColor }}>${s.cost.toLocaleString('es-AR')}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Staff Selection */}
        {business.staff?.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor, letterSpacing: 1, marginBottom: 10 }}>PROFESIONAL (OPCIONAL)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {business.staff.map(s => (
                <div key={s.id} data-testid={`booking-staff-${s.id}`}
                  onClick={() => setSelectedStaff(selectedStaff?.id === s.id ? null : s)}
                  style={{
                    border: `2px solid ${selectedStaff?.id === s.id ? s.color : 'rgba(255,255,255,0.15)'}`,
                    borderRadius: 20, padding: '8px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    background: selectedStaff?.id === s.id ? `${s.color}22` : 'transparent'
                  }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: s.color }} />
                  <span style={{ fontWeight: selectedStaff?.id === s.id ? 700 : 400 }}>{s.staff_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date Selection */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor, letterSpacing: 1, marginBottom: 10 }}>FECHA</div>
          <div style={{ position: 'relative' }}>
            <Calendar size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
            <input data-testid="booking-date-input" type="date" value={selectedDate} min={getMinDate()} onChange={e => setSelectedDate(e.target.value)}
              style={{ width: '100%', background: 'rgba(255,255,255,0.08)', border: `1px solid rgba(255,255,255,0.15)`, borderRadius: 10, padding: '12px 12px 12px 36px', color: '#fff', fontSize: '0.95rem', boxSizing: 'border-box' }} />
          </div>
        </div>

        {/* Time Slots */}
        {selectedDate && (
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: accentColor, letterSpacing: 1, marginBottom: 10 }}>HORARIO</div>
            {slotsLoading ? <div className="spinner" style={{ margin: '20px auto' }} /> :
             slotsClosed ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: 20 }}>El negocio está cerrado este día.</p> :
             slots.length === 0 ? <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: 20 }}>No hay horarios disponibles.</p> : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {slots.map(s => (
                  <div key={s.time} data-testid={`booking-slot-${s.time}`}
                    onClick={() => s.available && setSelectedSlot(s.time)}
                    style={{
                      padding: '10px 16px', borderRadius: 8, cursor: s.available ? 'pointer' : 'not-allowed',
                      border: `2px solid ${selectedSlot === s.time ? accentColor : s.available ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)'}`,
                      background: selectedSlot === s.time ? `${accentColor}33` : s.available ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                      color: s.available ? '#fff' : 'rgba(255,255,255,0.25)',
                      fontWeight: selectedSlot === s.time ? 700 : 400,
                      fontSize: '0.9rem', textDecoration: !s.available ? 'line-through' : 'none'
                    }}>
                    {s.time}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Submit */}
        <button data-testid="booking-submit-btn" onClick={handleSubmit} disabled={saving}
          style={{
            width: '100%', padding: 16, borderRadius: 12, border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
            background: `linear-gradient(135deg, ${accentColor}, ${accentColor}99)`,
            color: isEstetica ? '#fff' : '#0e1117', fontWeight: 800, fontSize: '1rem', letterSpacing: 0.5
          }}>
          {saving ? 'Reservando...' : 'Confirmar Turno'}
        </button>
      </div>
    </div>
  );
}
